import { ServingMethod } from '@app/core/hera-models';
import { Injectable } from '@angular/core';
import { MenuDataService, ShopDataService } from '@core/api/data-services';
import { catchAndMapError } from '@core/helpers';
import { Shop, Card, Product } from '@core/hera-models';
import { Formula } from '@core/api/models';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_TTL, MEAL_ORDER_STORAGE_KEY } from '@shared/constants';
import { GroupedProducts, Placement, ShopInitData } from '@shared/interfaces';
import { StorageService } from '@shared/services/storage.service';
import { PlacementService } from '@shared/services/placement.service';
import { getActiveClosingPeriod } from '@shared/utils/closing-period.util';
import { Order } from '@views/meal/shared/order';
import { forkJoin, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MealService {
  shop!: Shop;
  cards!: Card[];
  formulas!: Formula[];
  order: Order;

  dateSelected!: string;
  shopId!: string;
  tableId!: string;
  cardId?: string;
  sessionId?: string;
  productsByCategory!: GroupedProducts[];
  products!: Product[];
  sides!: Product[];
  isFormulaEdit = false;
  pm!: string;

  constructor(
    private menuDataService: MenuDataService,
    private shopDataService: ShopDataService,
    private translateService: TranslateService,
    private localStorageService: StorageService,
    private placementService: PlacementService
  ) {
    this.order = new Order();
  }

  /**
   * @deprecated inject {@link PlacementService} and use `getPlacement()` directly
   */
  get placement(): Placement | null {
    return this.placementService.getPlacement();
  }

  fetchShop(shopId: string, dateSelected: string): Observable<ShopInitData> {
    return this.shopDataService.getShop(shopId).pipe(
      catchAndMapError(),
      switchMap((shop) => {
        if (getActiveClosingPeriod(shop?.closing_periods)) {
          return of({ shop, cards: [], formulas: [] });
        }

        return forkJoin({
          shop: of(shop),
          cards: this.menuDataService
            .getCards(shopId, dateSelected)
            .pipe(catchAndMapError([])),
          formulas: this.menuDataService
            .getFormulas(shopId, dateSelected)
            .pipe(catchAndMapError([])),
        });
      })
    );
  }

  setMealInfo(body: { sessionId?: string; cardId?: string }): void {
    this.sessionId = body.sessionId;
    this.cardId = body.cardId;
  }

  getTableTitle(): string | null {
    const placement = this.placementService.getPlacement();
    if (!placement?.number) {
      return null;
    }
    const key =
      placement.kind === 'position'
        ? 'recapCommandePage.title3'
        : 'recapCommandePage.title2';
    return this.translateService.instant(key, {
      tableNumber: placement.number,
    });
  }

  storeOrder(): void {
    this.localStorageService.setSessionStorageItem(
      MEAL_ORDER_STORAGE_KEY,
      { ...this.order },
      DEFAULT_TTL
    );
  }

  isCurrentServingMethodCounterPicker(): boolean {
    return this.shop.serving_method === ServingMethod.CounterPickupService;
  }

  isCurrentServingMethodSequencial(): boolean {
    return this.shop.serving_method === ServingMethod.SequentialService;
  }

  isCurrentServingMethodComplete(): boolean {
    return this.shop.serving_method === ServingMethod.CompleteService;
  }
}
